import { useMutation, useQuery, useQueryClient } from 'react-query';
import { predictionManagerAxiosApi } from 'src/Config/axiosApi';

export interface SystemConfig {
  openerEngine: 'node' | 'rust';
  unwinderEngine: 'node' | 'rust';
  /**
   * Global default counter-hedge pricing mode. A per-rule `counterHedgeMode`
   * of `null` inherits this. Default `'gtc-to-max-sum'`.
   */
  counterHedgeModeDefault:
    | 'always-threshold'
    | 'gtc-to-max-sum'
    | 'market-to-hedge';
  /** Max combined sum the market-to-hedge mode will pay. Default `1.0`. */
  marketHedgeMaxSum: number;
  /**
   * Global default for "keep the Polymarket maker posted far from threshold"
   * (placement-gate disable). Terminal bottom of the rule ?? group ?? global
   * inherit chain. Concrete boolean, default `false` (= gate ON).
   */
  disablePlacementGatePolymarketDefault: boolean;
  /** Same as `disablePlacementGatePolymarketDefault` but for the Kalshi leg. */
  disablePlacementGateKalshiDefault: boolean;
  /**
   * Global default sweep dual-post params shared by all rules. Each field is
   * `number | null`; `null` means "use the engine default". The runtime
   * resolves `rule.<field> ?? global.<field> ?? engineDefault`.
   */
  sweepDefaults?: SweepDefaults;
}

/**
 * Global sweep-response defaults. Every field is nullable — `null` means the
 * engine's built-in default applies.
 */
export interface SweepDefaults {
  sweepWindowMs: number | null;
  sweepMinTicks: number | null;
  sweepMinLevelsCleared: number | null;
  sweepDepthFraction: number | null;
  sweepMinContractsFloor: number | null;
  sweepDeltaSigma: number | null;
  sweepConfirmMs: number | null;
  sweepFreshMs: number | null;
  sweepResolveMs: number | null;
  sweepKalshiOffsetLevels: number | null;
  sweepPolymarketOffsetLevels: number | null;
  sweepRefireMinTicks: number | null;
  adverseCancelTicks: number | null;
  liveMakerSizeFactor: number | null;
  sweepFallingHoldMs: number | null;
  sweepFallingBounceTicks: number | null;
  /**
   * Counter-feasibility floor on/off, encoded as `1` (on) / `0` (off).
   * `null` means "use the engine default" (on).
   */
  counterFeasibilityFloor: number | null;
  /**
   * Volume-confirmation gate on/off, encoded as `1` (on) / `0` (off).
   * `null` means "use the engine default" (off).
   */
  sweepRequireVolumeConfirmation: number | null;
  sweepVolumeConfirmRatio: number | null;
}

/**
 * Fetches the current engine choice + global hedge config from the
 * prediction-manager.
 * GET /api/system/engine → SystemConfig
 * Polled every 30s. Returns null until the first successful response.
 */
export function useSystemConfig() {
  return useQuery<SystemConfig>({
    queryKey: ['system-config'],
    queryFn: async () => {
      const res = await predictionManagerAxiosApi.get('/api/system/engine');
      return res.data as SystemConfig;
    },
    refetchInterval: 30_000,
    staleTime: 10_000,
  });
}

export interface UpdateHedgeModeDto {
  counterHedgeModeDefault?:
    | 'always-threshold'
    | 'gtc-to-max-sum'
    | 'market-to-hedge';
  marketHedgeMaxSum?: number;
  disablePlacementGatePolymarketDefault?: boolean;
  disablePlacementGateKalshiDefault?: boolean;
}

/**
 * Updates the global counter-hedge config.
 * POST /api/system/hedge-mode → { counterHedgeModeDefault?, marketHedgeMaxSum?,
 *   disablePlacementGatePolymarketDefault?, disablePlacementGateKalshiDefault? }
 * Invalidates the system-config query on success so the chips/controls refresh.
 */
export function useUpdateSystemConfig() {
  const qc = useQueryClient();
  return useMutation(
    async (dto: UpdateHedgeModeDto) => {
      const res = await predictionManagerAxiosApi.post(
        '/api/system/hedge-mode',
        dto
      );
      return res.data as SystemConfig;
    },
    {
      onSuccess: () => {
        qc.invalidateQueries(['system-config']);
      },
    }
  );
}

/**
 * Exposes the global sweep dual-post defaults from the system config.
 * GET /api/system/engine → SystemConfig.sweepDefaults
 */
export function useSweepDefaults() {
  const query = useSystemConfig();
  return { ...query, data: query.data?.sweepDefaults };
}

export type UpdateSweepDefaultsDto = Partial<SweepDefaults>;

/**
 * Updates the global sweep dual-post defaults.
 * PUT /api/system/sweep-defaults → Partial<SweepDefaults>
 * Invalidates the system-config query on success so the editor refreshes.
 */
export function useUpdateSweepDefaults() {
  const qc = useQueryClient();
  return useMutation(
    async (dto: UpdateSweepDefaultsDto) => {
      const res = await predictionManagerAxiosApi.put(
        '/api/system/sweep-defaults',
        dto
      );
      return res.data as SystemConfig;
    },
    {
      onSuccess: () => {
        qc.invalidateQueries(['system-config']);
      },
    }
  );
}
